export type ExtensionPhase = "idle" | "pairing" | "connecting" | "connected" | "stopping" | "stopped" | "error";

export type ExtensionStatus = {
  phase: ExtensionPhase;
  sessionId?: string;
  tabId?: number;
  tabTitle?: string;
  tabUrl?: string;
  error?: string;
};

export type ExtensionInfo = {
  installed: true;
  version: string;
  status: ExtensionStatus;
};

export type AutoConnectInput = {
  relayBaseUrl: string;
  sessionId: string;
  pairingCode: string;
  targetUrl: string;
};

type ExtensionMessage = {
  source?: string;
  type?: string;
  requestId?: string;
  protocolVersion?: number;
  version?: string;
  status?: ExtensionStatus;
};

const WEB_SOURCE = "echolab-web";
const EXTENSION_SOURCE = "echolab-extension";
const PROTOCOL_VERSION = 1;

export function detectExtension(timeoutMs = 1_200): Promise<ExtensionInfo | null> {
  return request("echolab.extension.ping", "echolab.extension.ready", undefined, timeoutMs).catch(() => null);
}

export function connectExtension(input: AutoConnectInput): Promise<ExtensionInfo> {
  return request("echolab.extension.connect", "echolab.extension.connection", input, 25_000);
}

export function subscribeExtension(listener: (info: ExtensionInfo) => void): () => void {
  const receive = (event: MessageEvent<ExtensionMessage>) => {
    const message = acceptedMessage(event);
    if (!message || (message.type !== "echolab.extension.status" && message.type !== "echolab.extension.announce")) return;
    listener(asInfo(message));
  };
  window.addEventListener("message", receive);
  return () => window.removeEventListener("message", receive);
}

function request(
  type: string,
  expectedType: string,
  payload: AutoConnectInput | undefined,
  timeoutMs: number,
): Promise<ExtensionInfo> {
  const requestId = crypto.randomUUID();
  return new Promise((resolve, reject) => {
    const receive = (event: MessageEvent<ExtensionMessage>) => {
      const message = acceptedMessage(event);
      if (!message || message.type !== expectedType || message.requestId !== requestId) return;
      cleanup();
      resolve(asInfo(message));
    };
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error(type === "echolab.extension.ping" ? "extension_not_detected" : "extension_connection_timeout"));
    }, timeoutMs);
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("message", receive);
    };
    window.addEventListener("message", receive);
    window.postMessage({ source: WEB_SOURCE, type, requestId, protocolVersion: PROTOCOL_VERSION, payload }, window.location.origin);
  });
}

function acceptedMessage(event: MessageEvent<ExtensionMessage>): ExtensionMessage | null {
  if (event.source !== window || event.origin !== window.location.origin) return null;
  const message = event.data;
  if (message?.source !== EXTENSION_SOURCE || message.protocolVersion !== PROTOCOL_VERSION || !message.version || !message.status) return null;
  return message;
}

function asInfo(message: ExtensionMessage): ExtensionInfo {
  return {
    installed: true,
    version: message.version!,
    status: message.status!,
  };
}
